// pages/UserSignUp.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/SetNavbar";
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  Grid,
  Alert,
  Box,
  Typography,
  Container,
  Paper,
  Chip,
  Fade,
  Stack,
  Divider,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Person,
  Email,
  Lock,
  Phone,
  Visibility,
  VisibilityOff,
  School,
  ArrowForward,
  Google,
  Facebook,
  LinkedIn,
  Verified,
  Star,
  CheckCircle,
} from "@mui/icons-material";
import { postData, sendEmail } from "../../services/Request";
import CircularProgress from "@mui/material/CircularProgress";
import S1 from "../../assets/ContactUs/S1.png";
import "../../styles/SignUp.css";

export default function UserSignUp() {
  const nav = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [alertMessage, setAlertMessage] = useState("");
  const [severity, setSeverity] = useState("");
  const [loader, setLoader] = useState(false);
  const [agree, setAgree] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form field states for validation
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    password: "",
    confirm_password: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Check if user is already logged in
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const data = JSON.parse(user);
        if (data && data.usertype === "Student") {
          nav("/courses");
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, [nav]);

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "firstname":
        if (!value.trim()) error = "First name is required";
        break;
      case "lastname":
        if (!value.trim()) error = "Last name is required";
        break;
      case "email":
        if (!value.trim()) {
          error = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "phone":
        if (!value.trim()) {
          error = "Phone number is required";
        } else if (!/^[\d\s-()+]+$/.test(value)) {
          error = "Please enter a valid phone number";
        }
        break;
      case "password":
        if (!value) {
          error = "Password is required";
        } else if (value.length < 6) {
          error = "Password must be at least 6 characters";
        }
        break;
      case "confirm_password":
        if (!value) {
          error = "Please confirm your password";
        } else if (value !== formData.password) {
          error = "Passwords do not match";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setAlertMessage("Please fix all errors before submitting");
      setSeverity("warning");
      return;
    }

    if (!agree) {
      setAlertMessage("Please agree to the Terms & Conditions");
      setSeverity("warning");
      return;
    }

    setLoader(true);
    setAlertMessage("");

    const details = {
      firstname: formData.firstname,
      lastname: formData.lastname,
      email: formData.email,
      password: formData.password,
      confirm_password: formData.confirm_password,
      phone: formData.phone,
    };

    const mailStudent = {
      fullName: formData.firstname + " " + formData.lastname,
      email: formData.email,
    };

    const mailAdmin = {
      fullName: formData.firstname + " " + formData.lastname,
      email: "wavepalmtechng@gmail.com",
    };

    postData("usersignup", details)
      .then((response) => {
        setLoader(false);
        if (response.status === true) {
          sendEmail("/mail.php", mailStudent);
          sendEmail("/mailAdmin.php", mailAdmin);
          setAlertMessage("✅ " + response.message);
          setSeverity("success");
          setTimeout(() => {
            nav("/userlogin");
          }, 1000);
        } else {
          setAlertMessage("❌ " + response.message);
          setSeverity("error");
        }
      })
      .catch((error) => {
        setLoader(false);
        if (error.request?.status === 422) {
          setAlertMessage(
            "❌ " + error.response?.data?.errors?.email || "Validation error",
          );
          setSeverity("error");
        } else if (error.request?.status === 500) {
          setAlertMessage(
            "❌ Connection to internet lost, please check connection and retry",
          );
          setSeverity("error");
        } else {
          setAlertMessage("❌ Error creating account");
          setSeverity("error");
        }
      });
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleAgreeChange = (event) => {
    setAgree(event.target.checked);
  };

  return (
    <>
      <Header />
      <Box
        className="signup-container"
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #0a1628 0%, #0d1f3c 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          py: { xs: 4, md: 0 },
        }}
      >
        {/* Animated Background Decorations */}
        <Box
          sx={{
            position: "absolute",
            top: -200,
            right: -200,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(78, 205, 196, 0.05) 0%, transparent 70%)",
            zIndex: 0,
            animation: "pulse 8s ease-in-out infinite",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -200,
            left: -200,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(69, 183, 209, 0.04) 0%, transparent 70%)",
            zIndex: 0,
            animation: "pulse 8s ease-in-out infinite 2s",
          }}
        />

        {/* Floating Particles */}
        {[...Array(4)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: `${10 + i * 25}%`,
              left: `${5 + (i % 3) * 30}%`,
              width: `${4 + i * 2}px`,
              height: `${4 + i * 2}px`,
              borderRadius: "50%",
              backgroundColor:
                i % 2 === 0
                  ? "rgba(78, 205, 196, 0.15)"
                  : "rgba(69, 183, 209, 0.15)",
              animation: `float ${6 + i * 2}s ease-in-out infinite ${i * 1.5}s`,
              zIndex: 0,
            }}
          />
        ))}

        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.06)",
              maxWidth: 1100,
              mx: "auto",
            }}
          >
            <Grid container>
              {/* Left Side - Image & Branding */}
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: { xs: "none", md: "flex" },
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                  background:
                    "linear-gradient(135deg, rgba(78, 205, 196, 0.05), rgba(69, 183, 209, 0.02))",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Image */}
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 400,
                  }}
                >
                  <Box
                    component="img"
                    src={S1}
                    alt="Student Sign Up"
                    sx={{
                      width: "100%",
                      height: "auto",
                      borderRadius: 3,
                      border: "1px solid rgba(255, 255, 255, 0.06)",
                      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                      transition: "transform 0.5s ease",
                      "&:hover": {
                        transform: "scale(1.02)",
                      },
                    }}
                  />

                  {/* Floating Badge */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 20,
                      left: 20,
                      backgroundColor: "rgba(10, 22, 40, 0.85)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(78, 205, 196, 0.2)",
                      borderRadius: 2,
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "rgba(78, 205, 196, 0.15)",
                        borderRadius: "50%",
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <School sx={{ color: "#4ECDC4" }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(255,255,255,0.3)" }}
                      >
                        Join 10,000+
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#ffffff", fontWeight: 700 }}
                      >
                        Active Students
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Trust Badges */}
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ mt: 4, flexWrap: "wrap", justifyContent: "center" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Verified sx={{ color: "#4ECDC4", fontSize: 16 }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.3)" }}
                    >
                      Secure Sign Up
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Star sx={{ color: "#FFD93D", fontSize: 16 }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.3)" }}
                    >
                      4.8/5 Rating
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CheckCircle sx={{ color: "#4ECDC4", fontSize: 16 }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.3)" }}
                    >
                      Free Trial
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* Right Side - Sign Up Form */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                  {/* Header */}
                  <Box sx={{ mb: 3 }}>
                    <Chip
                      label="🚀 Create Account"
                      sx={{
                        backgroundColor: "rgba(78, 205, 196, 0.15)",
                        color: "#4ECDC4",
                        fontWeight: 600,
                        mb: 2,
                        border: "1px solid rgba(78, 205, 196, 0.2)",
                      }}
                    />
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "#ffffff",
                        fontSize: { xs: "1.8rem", md: "2.2rem" },
                      }}
                    >
                      Get Started Today!
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255, 255, 255, 0.5)",
                        mt: 0.5,
                      }}
                    >
                      Embark on your tech journey! Sign up for real-time
                      sessions with expert tutors and start learning instantly.
                    </Typography>
                  </Box>

                  {/* Alert Message */}
                  {alertMessage && (
                    <Fade in timeout={300}>
                      <Alert
                        variant="filled"
                        severity={severity}
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          backgroundColor:
                            severity === "success" ? "#4ECDC4" : "#FF6B6B",
                          "& .MuiAlert-icon": {
                            color: "#ffffff",
                          },
                        }}
                      >
                        {alertMessage}
                      </Alert>
                    </Fade>
                  )}

                  {/* Form */}
                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="firstname"
                          label="First Name"
                          value={formData.firstname}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.firstname}
                          helperText={errors.firstname}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person
                                  sx={{ color: "rgba(255,255,255,0.2)" }}
                                />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(255, 255, 255, 0.04)",
                              color: "#ffffff",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.06)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.12)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#4ECDC4",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "rgba(255, 255, 255, 0.4)",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#FF6B6B",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="lastname"
                          label="Last Name"
                          value={formData.lastname}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.lastname}
                          helperText={errors.lastname}
                          InputProps={{
                            startAdornment: (
                              <Person sx={{ color: "rgba(255,255,255,0.2)" }} />
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(255, 255, 255, 0.04)",
                              color: "#ffffff",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.06)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.12)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#4ECDC4",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "rgba(255, 255, 255, 0.4)",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#FF6B6B",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="email"
                          label="Email Address"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.email}
                          helperText={errors.email}
                          InputProps={{
                            startAdornment: (
                              <Email sx={{ color: "rgba(255,255,255,0.2)" }} />
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(255, 255, 255, 0.04)",
                              color: "#ffffff",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.06)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.12)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#4ECDC4",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "rgba(255, 255, 255, 0.4)",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#FF6B6B",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="phone"
                          label="Phone Number"
                          value={formData.phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.phone}
                          helperText={errors.phone}
                          InputProps={{
                            startAdornment: (
                              <Phone sx={{ color: "rgba(255,255,255,0.2)" }} />
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(255, 255, 255, 0.04)",
                              color: "#ffffff",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.06)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.12)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#4ECDC4",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "rgba(255, 255, 255, 0.4)",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#FF6B6B",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="password"
                          label="Password"
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.password}
                          helperText={errors.password}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Lock sx={{ color: "rgba(255,255,255,0.2)" }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={handleTogglePassword}
                                  edge="end"
                                  sx={{ color: "rgba(255,255,255,0.2)" }}
                                >
                                  {showPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(255, 255, 255, 0.04)",
                              color: "#ffffff",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.06)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.12)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#4ECDC4",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "rgba(255, 255, 255, 0.4)",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#FF6B6B",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="confirm_password"
                          label="Confirm Password"
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirm_password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.confirm_password}
                          helperText={errors.confirm_password}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Lock sx={{ color: "rgba(255,255,255,0.2)" }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={handleToggleConfirmPassword}
                                  edge="end"
                                  sx={{ color: "rgba(255,255,255,0.2)" }}
                                >
                                  {showConfirmPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(255, 255, 255, 0.04)",
                              color: "#ffffff",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.06)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.12)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#4ECDC4",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "rgba(255, 255, 255, 0.4)",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#FF6B6B",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={agree}
                          onChange={handleAgreeChange}
                          sx={{
                            color: "rgba(255,255,255,0.2)",
                            "&.Mui-checked": {
                              color: "#4ECDC4",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ color: "rgba(255,255,255,0.3)" }}
                        >
                          I agree to the{" "}
                          <Link
                            href="#"
                            sx={{
                              color: "#4ECDC4",
                              textDecoration: "none",
                              "&:hover": {
                                color: "#45B7D1",
                              },
                            }}
                          >
                            Terms & Conditions
                          </Link>
                        </Typography>
                      }
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loader || !agree}
                      startIcon={!loader ? <ArrowForward /> : null}
                      sx={{
                        mt: 2,
                        mb: 2,
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: 700,
                        background: "linear-gradient(135deg, #4ECDC4, #45B7D1)",
                        borderRadius: 2,
                        boxShadow: "0 4px 25px rgba(78, 205, 196, 0.3)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 35px rgba(78, 205, 196, 0.4)",
                          background:
                            "linear-gradient(135deg, #45B7D1, #4ECDC4)",
                        },
                        "&:disabled": {
                          backgroundColor: "rgba(78, 205, 196, 0.2)",
                        },
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      {loader ? (
                        <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                      ) : (
                        "Create Account"
                      )}
                    </Button>

                    {/* Divider */}
                    <Box sx={{ my: 2 }}>
                      <Divider
                        sx={{ backgroundColor: "rgba(255,255,255,0.06)" }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255,255,255,0.2)", px: 2 }}
                        >
                          Or continue with
                        </Typography>
                      </Divider>
                    </Box>

                    {/* Login Link */}
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.3)" }}
                      >
                        Already have an account?{" "}
                        <Link
                          href="/userlogin"
                          sx={{
                            color: "#4ECDC4",
                            fontWeight: 600,
                            textDecoration: "none",
                            "&:hover": {
                              color: "#45B7D1",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Login
                        </Link>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>

        <style>
          {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.3; }
            50% { transform: scale(1.1); opacity: 0.6; }
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }

          .signup-container {
            min-height: 100vh;
          }
        `}
        </style>
      </Box>
    </>
  );
}
